import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken } from '../../services/mapService';
import './MultiMarkerMap.css';

/**
 * MultiMarkerMap component for showing multiple locations on a map
 * @param {Object} props
 * @param {Array} props.locations - Array of location objects with id, lat, lng, name
 * @param {Function} props.onMarkerClick - Callback when a marker is clicked
 * @param {string} props.selectedLocationId - ID of the currently selected location
 * @param {string} props.className - Additional CSS class names
 */
const MultiMarkerMap = ({ 
  locations = [], 
  onMarkerClick, 
  selectedLocationId = null,
  className = ''
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef({});
  const [mapInitialized, setMapInitialized] = useState(false);
  
  // Initialize map
  useEffect(() => {
    if (map.current) return; // Skip if map is already initialized
    
    const token = getMapboxToken();
    if (!token) {
      console.error('Mapbox token not found');
      return;
    }
    
    // Set the access token
    mapboxgl.accessToken = token;
    
    // Create the map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-74.0060, 40.7128], // Default to New York
      zoom: 2
    });
    
    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Set a flag when the map is loaded
    map.current.on('load', () => {
      setMapInitialized(true);
    });
    
    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);
  
  // Update markers when locations change or map is initialized
  useEffect(() => {
    if (!map.current || !mapInitialized || !locations.length) return;
    
    // Clear existing markers
    Object.values(markers.current).forEach(marker => marker.remove());
    markers.current = {};
    
    // Calculate bounds
    const bounds = new mapboxgl.LngLatBounds();
    
    // Add new markers
    locations.forEach(location => {
      if (!location.lat || !location.lng) return;
      
      // Check if we need to highlight this marker
      const isSelected = selectedLocationId && location.id === selectedLocationId;
      
      // Create marker element
      const el = document.createElement('div');
      el.className = `marker ${isSelected ? 'selected' : ''}`;
      
      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 })
        .setHTML(`<h3>${location.name}</h3>`);
      
      // Create marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.lng, location.lat])
        .setPopup(popup)
        .addTo(map.current);
      
      // Add click handler
      if (onMarkerClick) {
        el.addEventListener('click', () => {
          onMarkerClick(location);
        });
      }
      
      // Store marker reference
      markers.current[location.id] = marker;
      
      // Extend bounds
      bounds.extend([location.lng, location.lat]);
    });
    
    // Fit map to bounds if we have more than one location
    if (locations.length > 1) {
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15
      });
    } else if (locations.length === 1) {
      // If we only have one location, center on it
      map.current.setCenter([locations[0].lng, locations[0].lat]);
      map.current.setZoom(12);
    }
  }, [locations, mapInitialized, onMarkerClick, selectedLocationId]);
  
  // Update marker selection when selectedLocationId changes
  useEffect(() => {
    if (!map.current || !mapInitialized) return;
    
    // Update marker styles based on selection
    Object.entries(markers.current).forEach(([id, marker]) => {
      const el = marker.getElement();
      if (selectedLocationId && id === selectedLocationId) {
        el.classList.add('selected');
        
        // Open popup for selected marker
        marker.togglePopup();
        
        // Center map on selected marker
        map.current.flyTo({
          center: marker.getLngLat(),
          zoom: 12,
          essential: true
        });
      } else {
        el.classList.remove('selected');
        // Close popup if open
        if (marker.getPopup().isOpen()) {
          marker.togglePopup();
        }
      }
    });
  }, [selectedLocationId, mapInitialized]);
  
  return (
    <div 
      ref={mapContainer} 
      className={`multi-marker-map ${className}`}
      data-testid="multi-marker-map"
    />
  );
};

export default MultiMarkerMap; 